import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import Select from "components/Select";
import { getSelectStyles } from "../../utils/selectStyles";
import { useTheme2FormPage } from "hooks/useTheme2FormPage";
import { useGetInventoryId, useCreateInventory, useGetInventorySettings } from "../../api/inventory/service/index";
import ReactDatetime from "components/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import CustomInputOrSelect from "../../components/CustomInputOrSelect";
import { formatPriceWithDollar } from '../../utils/currency';
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import { useGetChannels } from "api/channels/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useGetStatuses } from "api/inventory-status/service";
import { useNotification } from "../../contexts/NotificationContext";
import InventoryRightDrawer from "./components/InventoryRightDrawer";
import SubHeader from "../../components/Navbars/SubHeader";


const AddInventoryInformation = ({ vin, activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const {
    inputStyle,
    selectStyles: theme2SelectStyles,
    formFieldsClassName,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "add-inventory",
    withSelectStyles: true,
  });
  const [isLightTheme, setIsLightTheme] = useState(false);
    const { data: inventorySettings, isLoading: inventorySettingsLoading } = useGetInventorySettings();

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(
    () => getSelectStyles(activeColorStyle, isLightTheme),
    [activeColorStyle, isLightTheme]
  );
  const resolvedSelectStyles = useMemo(() => {
    if (theme2SelectStyles) {
      return {
        ...theme2SelectStyles,
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      };
    }
    return customSelectStyles;
  }, [theme2SelectStyles, customSelectStyles]);

  const navigate = useNavigate();
  const { data, isLoading } = useGetInventoryId(vin, true);
  const vpic = data?.vpicData?.Results?.[0] || {};
  const createInventoryMutation = useCreateInventory();
  const { data: buyers } = useGetChannels("inventory_buyer");
  const { data: locationsData } = useGetChannels("sales_location_branch");
  const locationOptions = useMemo(
    () =>
      (locationsData?.results || [])
        .filter((location) => location?.type === "sales_location_branch")
        .map((location) => ({
          value: String(location.id),
          label: location.name,
        })),
    [locationsData]
  );

  const [isCustomEngine, setIsCustomEngine] = useState(false);
  const [isCustomDriveType, setIsCustomDriveType] = useState(false);
  const [isCustomYear, setIsCustomYear] = useState(false);
  const [isCustomMake, setIsCustomMake] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [isCustomTransmission, setIsCustomTransmission] = useState(false);
  const [isCustomFuelType, setIsCustomFuelType] = useState(false);
  const [isCustomBodyStyle, setIsCustomBodyStyle] = useState(false);
  const [isCustomInteriorColor, setIsCustomInteriorColor] = useState(false);
  const [isCustomExteriorColor, setIsCustomExteriorColor] = useState(false);
  const [isCustomMileageStatus, setIsCustomMileageStatus] = useState(false);
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [showAddLot, setShowAddLot] = useState(false);
  const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();
  const [requiredFieldsTouched, setRequiredFieldsTouched] = useState({});
  const [prevValues, setPrevValues] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);



  const [formData, setFormData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    bodyStyle: '',
    driveType: '',
    transmission: '',
    engine: '',
    fuelType: '',
    type: '',
    exteriorColor: '',
    interiorColor: '',
    acquiredMileage: '',
    mileageStatus: '',
    exteriorPrimaryColor: '',
    exteriorSecondaryColor: '',
    vehicleLot: '',
    buyer: '',
    dateAcquired: new Date(),
    acquiredPrice: 0,
    acquiredRefNumber: '',
    acquiredFrom: 'unknown',
    acquiredFromName: '',
    acquiredFromNameChannel: '',
    paymentMethod: '',
    vin: vin,
    vehicleStatus: '',
  });


  useEffect(() => {
    // Restore formData from sessionStorage if available
    const savedFormData = sessionStorage.getItem('addInventoryFormData');
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
      sessionStorage.removeItem('addInventoryFormData');
      return;
    }
    if (data) {
      const vpic = data?.vpicData?.Results?.[0] || {};
      const inventory = data?.inventory || {};

      let engineString = '';
      if (vpic.DisplacementL) {
        engineString += vpic.DisplacementL + 'L ';
      }
      if (vpic.EngineConfiguration) {
        engineString += vpic.EngineConfiguration + ' ';
      } else if (vpic.EngineCylinders) {
        engineString += vpic.EngineCylinders + ' Cyl ';
      }
      if (vpic.ValveTrainDesign) {
        engineString += vpic.ValveTrainDesign + ' ';
      } else if (vpic.OtherEngineInfo) {
        engineString += vpic.OtherEngineInfo + ' ';
      }
      engineString = engineString.trim();

      setFormData({
        year: vpic.ModelYear || '',
        make: vpic.Make ? vpic.Make.charAt(0).toUpperCase() + vpic.Make.slice(1).toLowerCase() : '',
        model: vpic.Model || '',
        trim: vpic.Trim || '',
        bodyStyle: vpic.BodyClass || "",
        driveType: vpic.DriveType ? vpic.DriveType.charAt(0).toUpperCase() + vpic.DriveType.slice(1).toLowerCase() : "",
        transmission: vpic.Transmission || "",
        engine: engineString || '',
        fuelType: vpic.FuelTypePrimary || "",
        type: vpic.Type || 'Unknown',
        exteriorColor: vpic.ExteriorColor || '',
        interiorColor: vpic.InteriorColor || '',
        acquiredMileage: inventory.mileage || '',
        mileageStatus: inventory.mileage_status || '',
        exteriorPrimaryColor: vpic.ExteriorPrimaryColor || '',
        exteriorSecondaryColor: vpic.ExteriorSecondaryColor || '',
        buyer: inventory.buyer || '',
        dateAcquired: inventory.date_acquired || new Date(),
        acquiredPrice: inventory.acquired_price || 0,
        acquiredRefNumber: inventory.acquired_ref_number || '',
        acquiredFrom: inventory.acquired_from || 'unknown',
        acquiredFromName: inventory.acquired_from_name || '',
        acquiredFromNameChannel: inventory.acquired_from_name_channel || '',
        paymentMethod: inventory.payment_method || '',
        vin: vin,
        vehicleStatus: inventory.vehicle_status || '',
      });
    }
  }, [data, vin]);

  useEffect(() => {
    if (formData.vehicleLot || inventorySettingsLoading || locationOptions.length === 0) {
      return;
    }

    const defaultLocationId = inventorySettings?.default_location?.id || inventorySettings?.default_location;
    const hasDefaultInOptions = defaultLocationId
      ? locationOptions.some((option) => option.value === String(defaultLocationId))
      : false;

    const nextVehicleLot = hasDefaultInOptions
      ? String(defaultLocationId)
      : locationOptions[0].value;

    setFormData((prev) => ({ ...prev, vehicleLot: nextVehicleLot }));
  }, [locationOptions, formData.vehicleLot, inventorySettings, inventorySettingsLoading]);

  useEffect(() => {
    if (statusesData?.results && !formData.vehicleStatus && !inventorySettingsLoading) {
      // First check if inventorySettings has a default status_for_inventory
      const defaultStatusId = inventorySettings?.status_for_inventory?.id || inventorySettings?.status_for_inventory;
      if (defaultStatusId) {
        setFormData((prev) => ({ ...prev, vehicleStatus: defaultStatusId }));
      } else {
        // Fallback to "available" status
        const availableStatus = statusesData.results.find(
          (s) => s.status_key === "available" || s.status_name.toLowerCase() === "available"
        );
        if (availableStatus) {
          setFormData((prev) => ({ ...prev, vehicleStatus: availableStatus.id }));
        }
      }
    }
  }, [statusesData, formData, isLoadingStatuses, inventorySettings, inventorySettingsLoading]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleBuyerChange = (value) => {
    if (value === "add_new_buyer") {
      setShowAddBuyer(true);
    } else {
      handleInputChange('buyer', value);
    }
  };



  const handleLocationChange = (value) => {
    if (value === "add_new_lot") {
      setShowAddLot(true);
    } else {
      handleInputChange('vehicleLot', value);
    }
  };
  const handleSave = () => {
    let hasError = false;
    const missing = {};
    const missingFields = [];

    if (!formData?.vehicleLot || formData.vehicleLot === '') {
      hasError = true;
      missing.vehicleLot = true;
      missingFields.push(t('addInventoryInformation.vehicleLot'));
    }
    if (!formData?.acquiredMileage) {
      hasError = true;
      missing.acquiredMileage = true;
      missingFields.push(t('addInventoryInformation.acquiredMileage'));
    }
    if (!formData?.mileageStatus) {
      hasError = true;
      missing.mileageStatus = true;
      missingFields.push(t('addInventoryInformation.mileageStatus'));
    }
    if (!formData?.acquiredPrice) {
      hasError = true;
      missing.acquiredPrice = true;
      missingFields.push(t('addInventoryInformation.acquiredPrice'));
    }
    if (!formData?.vehicleStatus) {
      hasError = true;
      missing.vehicleStatus = true;
      missingFields.push(t('addInventoryInformation.vehicleStatus'));
    }

    setRequiredFieldsTouched(missing);

    if (hasError) {
      // Eksik alanları güzel bir mesajla göster
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('addInventoryInformation.requiredFieldsMissing')}: ${fieldsList}`
      );
      return;
    }

    // Eski dosyadaki data formatı - sadece dolu alanları gönder
    const inventoryPayload = {
      vin: vin,
      ...(formData.model && { model: formData.model }),
      ...(formData.acquiredPrice && !isNaN(Number(formData.acquiredPrice.toString().replace(/[^\d.]/g, '')))
        ? { cost: parseFloat(formData.acquiredPrice.toString().replace(/[^\d.]/g, '')) }
        : {}),
      ...(formData.make && { make: formData.make }),
      ...(formData.year && { year: formData.year }),
      ...(formData.trim && { trim: formData.trim }),
      ...(formData.exteriorColor && { exterior_color: formData.exteriorColor }),
      ...(formData.exteriorPrimaryColor && { exterior_primary_color: formData.exteriorPrimaryColor }),
      ...(formData.exteriorSecondaryColor && { exterior_secondary_color: formData.exteriorSecondaryColor }),
      ...(formData.bodyStyle && { body_style: formData.bodyStyle }),
      ...(formData.driveType && { drive_type: formData.driveType }),
      ...(formData.fuelType && { fuel: formData.fuelType }),
      ...(formData.acquiredMileage && { actual_mileage: formData.acquiredMileage }),
      ...(formData.mileageStatus && { actual_mileage_status: formData.mileageStatus }),
      ...(formData.acquiredMileage && { acquired_mileage: formData.acquiredMileage }),
      ...(formData.mileageStatus && { acquired_mileage_status: formData.mileageStatus }),
      ...(formData.engine && { engine: formData.engine }),
      ...(formData.transmission && { transmission: formData.transmission }),
      ...(formData.acquiredRefNumber && { acquired_ref_number: formData.acquiredRefNumber }),
      ...(formData.dateAcquired && { acquired_date: moment(formData.dateAcquired).format('YYYY-MM-DD') }),
      ...(formData.paymentMethod && { payment_method: formData.paymentMethod }),
      ...(formData.type && { type: formData.type }),
      ...(formData.vehicleLot && { location: formData.vehicleLot }),
      ...(formData.buyer && { buyer: formData.buyer }),
      ...(formData.vehicleStatus && { vehicle_status: formData.vehicleStatus }),
      ...(formData.acquiredFrom && { acquired_from: formData.acquiredFrom }),
      ...(formData.acquiredFromName && { acquired_from_name: formData.acquiredFromName }),
      ...(formData.acquiredFrom === 'dealer' && formData.acquired_from_name_channel && { acquired_from_name_channel: formData.acquired_from_name_channel }),
      ...(formData.acquiredFrom === 'individual' && formData.acquired_from_name_customer && { acquired_from_name_customer: formData.acquired_from_name_customer }),
    };


    // useCreateInventory ile aynı şekilde API çağrısı
    createInventoryMutation.mutate(inventoryPayload);
  };

  if (isLoading) {
    return (
      <div className="content">
        <Card>
          <CardBody className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-3">Loading vehicle information...</p>
          </CardBody>
        </Card>
      </div>
    );
  }


  const transmissions = [
    { value: "Manual", label: "Manual" },
    { value: "Automatic", label: "Automatic" },
    { value: "CVT", label: "CVT (Continuously Variable)" }
  ];
  const fuelTypes = vpic.FuelTypePrimary ? [{ value: vpic.FuelTypePrimary, label: vpic.FuelTypePrimary }] : [];
  const vehicleTypes = [
    { value: 'car', label: 'Car (Vehicle)' },
    { value: 'truck', label: 'Truck (Vehicle)' },
    { value: 'motorcycle', label: 'Motorcycle' },
    { value: 'recreational_vehicle', label: 'Recreational Vehicle' },
    { value: 'boat', label: 'Boat' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'aircraft', label: 'Aircraft' },
    { value: 'powersports', label: 'Powersports' },
    { value: 'other', label: 'Other' },
    { value: 'suv', label: 'SUV' },
    { value: 'van', label: 'Van' },
  ];
  const exteriorColors = [
    { value: "Beige", label: "Beige" },
    { value: "Black", label: "Black" },
    { value: "Blue", label: "Blue" },
    { value: "Brown", label: "Brown" },
    { value: "Gold", label: "Gold" },
    { value: "Gray", label: "Gray" },
    { value: "Green", label: "Green" },
    { value: "Maroon", label: "Maroon" },
    { value: "Orange", label: "Orange" },
    { value: "Pink", label: "Pink" },
    { value: "Purple", label: "Purple" },
    { value: "Red", label: "Red" },
    { value: "Silver", label: "Silver" },
    { value: "Tan", label: "Tan" },
    { value: "White", label: "White" },
    { value: "Yellow", label: "Yellow" }
  ];
  const interiorColors = [
    { value: "Black", label: "Black" },
    { value: "Gray", label: "Gray" },
    { value: "Beige", label: "Beige" },
    { value: "Brown", label: "Brown" }
  ];
  const mileageStatuses = [
    { value: "Actual", label: "Actual" },
    { value: "Exempt", label: "Exempt" },
    { value: "Not Actual", label: "Not Actual" }
  ];
  const acquiredFromOptions = [
    { value: "dealer", label: "Dealer/Wholesaler/Auction" },
    { value: "individual", label: "Individual" },
    { value: "unknown", label: "Unknown" }
  ];
  const paymentMethods = [
    { value: 'ach', label: 'ACH' },
    { value: 'ap', label: 'AP' },
    { value: 'arm', label: 'Arm' },
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'draft', label: 'Draft' },
    { value: 'floor_plan', label: 'Floor Plan' },
    { value: 'repossession', label: 'Repossession' },
    { value: 'trade_in', label: 'Trade-In' },
  ];

  return (
    <>
      {showAddBuyer && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="inventory_buyer"
        onClose={() => setShowAddBuyer(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("buyer", String(created.id));
          } else if (created?.name) {
            handleInputChange("buyer", created.name);
          }
        }}
      />}
      {showAddLot && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="sales_location_branch"
        onClose={() => setShowAddLot(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("vehicleLot", String(created.id));
          } else if (created?.name) {
            handleInputChange("vehicleLot", created.name);
          }
        }}
      />}
      {!showAddBuyer && !showAddLot &&
        <>
        <SubHeader
          title={`New Inventory: ${vpic.VIN} ${vpic.ModelYear} ${vpic.Make} ${vpic.Model} ${vpic.Series} ${vpic.Trim}`}
          back={-1}
          link={false}
        />
          <Row>
            <Col md={12} className="">
              <Card>
                <CardHeader className="font-weight-bold text-uppercase">
                  {t('addInventoryInformation.purchaseInformation')}
                </CardHeader>
                <CardBody>
                  <Form className={formFieldsClassName}>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.vehicleLot')} <span style={{ color: 'red' }}>*</span></Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectLot')}
                            name="vehicleLot"
                            isSearchable
                            closeMenuOnSelect={true}
                            value={formData.vehicleLot ? {
                              value: formData.vehicleLot,
                              label: formData.vehicleLot === "add_new_lot"
                                ? t('addInventoryInformation.addNewLot')
                                : locationOptions.find((loc) => loc.value === String(formData.vehicleLot))?.label || formData.vehicleLot
                            } : null}
                            onChange={(option) => {
                              if (option?.value === "add_new_lot") {
                                handleLocationChange("add_new_lot");
                              } else {
                                handleLocationChange(option?.value || "");
                              }
                            }}
                            options={[
                              { value: "add_new_lot", label: t('addInventoryInformation.addNewLot') },
                              ...locationOptions
                            ]}
                            isClearable={false}
                          />
                          {requiredFieldsTouched.vehicleLot && (
                            <div style={{ color: 'red', fontSize: 12 }}>{t('addInventoryInformation.required')}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.buyer')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectBuyer')}
                            name="buyer"
                            isSearchable
                            closeMenuOnSelect={true}
                            value={formData.buyer ? {
                              value: formData.buyer,
                              label: formData.buyer === "add_new_buyer"
                                ? t('addInventoryInformation.addNewBuyer')
                                : buyers?.results?.find(buyer => String(buyer.id) === formData.buyer)?.name || formData.buyer
                            } : null}
                            onChange={(option) => {
                              if (option?.value === "add_new_buyer") {
                                handleBuyerChange("add_new_buyer");
                              } else {
                                handleBuyerChange(option?.value || "");
                              }
                            }}
                            options={[
                              { value: "add_new_buyer", label: t('addInventoryInformation.addNewBuyer') },
                              ...(buyers?.results?.map((buyer) => ({
                                value: String(buyer.id),
                                label: buyer.name
                              })) || [])
                            ]}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.dateAcquired')}</Label>
                          <ReactDatetime
                            inputProps={{
                              className: "form-control",
                              placeholder: t('addInventoryInformation.datePlaceholder'),
                              style: inputStyle
                            }}
                            value={formData.dateAcquired}
                            onChange={(date) => handleInputChange('dateAcquired', date)}
                            dateFormat="MM-DD-YYYY"
                            timeFormat={false}
                            closeOnSelect={true}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.acquiredPrice')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input
                            style={inputStyle}
                            type="text"
                            value={formData.acquiredPrice}
                            onChange={e => {
                              // Sadece rakam ve nokta kabul et
                              const raw = e.target.value.replace(/[^\d.]/g, '');
                              handleInputChange('acquiredPrice', raw);
                            }}
                            onBlur={e => {
                              handleInputChange('acquiredPrice', formatPriceWithDollar(formData.acquiredPrice));
                            }}
                            onFocus={e => {
                              // Formatlıysa, sadece rakamları göster
                              const raw = formData.acquiredPrice.toString().replace(/[^\d.]/g, '');
                              handleInputChange('acquiredPrice', raw);
                            }}
                            invalid={!!requiredFieldsTouched.acquiredPrice}
                          />
                          {requiredFieldsTouched.acquiredPrice && (
                            <div style={{ color: 'red', fontSize: 12 }}>{t('addInventoryInformation.required')}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.acquiredRefNumber')}</Label>
                          <Input
                            style={inputStyle}
                            type="text"
                            value={formData.acquiredRefNumber}
                            onChange={(e) => handleInputChange('acquiredRefNumber', e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.acquiredFrom')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectSource')}
                            name="acquiredFrom"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.acquiredFrom ? {
                              value: formData.acquiredFrom,
                              label: acquiredFromOptions.find(opt => opt.value === formData.acquiredFrom)?.label || formData.acquiredFrom
                            } : null}
                            onChange={(option) => {
                              handleInputChange('acquiredFrom', option?.value || '');
                              if (option?.value === "dealer" || option?.value === "individual") {
                                setIsDrawerOpen(true);
                              }
                            }}
                            options={acquiredFromOptions}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.acquiredFromName')}</Label>
                          <div className="d-flex align-items-center">
                            <Input
                              style={inputStyle}
                              type="text"
                              value={formData.acquiredFromName}
                              onChange={(e) => handleInputChange('acquiredFromName', e.target.value)}
                              disabled={formData.acquiredFrom === "dealer" || formData.acquiredFrom === "individual" || formData.acquiredFrom === "unknown"}
                            />
                            {(formData.acquiredFrom === "dealer" || formData.acquiredFrom === "individual") && (
                              <Button
                                color="link"
                                className="p-0 ml-2"
                                style={{
                                  minWidth: 'auto',
                                  boxShadow: 'none',
                                  color: isLightTheme ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)'
                                }}
                                onClick={() => setIsDrawerOpen(true)}
                              >
                                <i className="tim-icons icon-zoom-split" />
                              </Button>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.paymentMethod')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectMethod')}
                            name="paymentMethod"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.paymentMethod ? {
                              value: formData.paymentMethod,
                              label: paymentMethods.find(method => method.value === formData.paymentMethod)?.label || formData.paymentMethod
                            } : null}
                            onChange={(option) => handleInputChange('paymentMethod', option?.value || '')}
                            options={paymentMethods}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>

            {/* Vehicle Information Card */}
            <Col md={6} >
              <Card>
                <CardHeader className="font-weight-bold text-uppercase">
                  {t('addInventoryInformation.vehicleInformation')}
                </CardHeader>
                <CardBody>
                  <Form className={formFieldsClassName}>
                    <Row>
                      <Col md={6}>
                        {/* Year Model */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.yearModel')}
                          value={formData.year}
                          onChange={val => handleInputChange('year', val)}
                          options={[]}
                          isCustom={isCustomYear}
                          setIsCustom={setIsCustomYear}
                          prevValue={prevValues.year}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, year: val }))}
                          fieldKey="year"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomYear')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                      <Col md={6}>
                        {/* Make */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.make')}
                          value={formData.make}
                          style={inputStyle}
                          onChange={val => handleInputChange('make', val)}
                          options={[]}
                          isCustom={isCustomMake}
                          setIsCustom={setIsCustomMake}
                          prevValue={prevValues.make}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, make: val }))}
                          fieldKey="make"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomMake')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        {/* Model */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.model')}
                          value={formData.model}
                          style={inputStyle}
                          onChange={val => handleInputChange('model', val)}
                          options={[]}
                          isCustom={isCustomModel}
                          setIsCustom={setIsCustomModel}
                          prevValue={prevValues.model}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, model: val }))}
                          fieldKey="model"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomModel')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                      <Col md={6}>
                        {/* Body Style */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.bodyStyle')}
                          value={formData.bodyStyle}
                          style={inputStyle}
                          onChange={val => handleInputChange('bodyStyle', val)}
                          options={[]}
                          isCustom={isCustomBodyStyle}
                          setIsCustom={setIsCustomBodyStyle}
                          prevValue={prevValues.bodyStyle}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, bodyStyle: val }))}
                          fieldKey="bodyStyle"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomBodyStyle')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        {/* Drive Type */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.driveType')}
                          value={formData.driveType}
                          style={inputStyle}
                          onChange={val => handleInputChange('driveType', val)}
                          options={[]}
                          isCustom={isCustomDriveType}
                          setIsCustom={setIsCustomDriveType}
                          prevValue={prevValues.driveType}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, driveType: val }))}
                          fieldKey="driveType"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomDriveType')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                      <Col md={6}>
                        {/* Transmission */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.transmission')}
                          value={formData.transmission}
                          style={inputStyle}
                          onChange={val => handleInputChange('transmission', val)}
                          options={transmissions}
                          isCustom={isCustomTransmission}
                          setIsCustom={setIsCustomTransmission}
                          prevValue={prevValues.transmission}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, transmission: val }))}
                          fieldKey="transmission"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomTransmission')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        {/* Engine */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.engine')}
                          value={formData.engine}
                          style={inputStyle}
                          onChange={val => handleInputChange('engine', val)}
                          options={[]}
                          isCustom={isCustomEngine}
                          setIsCustom={setIsCustomEngine}
                          prevValue={prevValues.engine}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, engine: val }))}
                          fieldKey="engine"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomEngine')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                      <Col md={6}>
                        {/* Fuel Type */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.fuelType')}
                          value={formData.fuelType}
                          style={inputStyle}
                          onChange={val => handleInputChange('fuelType', val)}
                          options={fuelTypes}
                          isCustom={isCustomFuelType}
                          setIsCustom={setIsCustomFuelType}
                          prevValue={prevValues.fuelType}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, fuelType: val }))}
                          fieldKey="fuelType"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomFuelType')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.type')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectType')}
                            name="type"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.type ? {
                              value: formData.type,
                              label: vehicleTypes.find(type => type.value === formData.type)?.label || formData.type
                            } : null}
                            onChange={(option) => handleInputChange('type', option?.value || '')}
                            options={vehicleTypes}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.vehicleStatus')} <span>*</span></Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectStatus')}
                            name="vehicleStatus"
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.vehicleStatus ? {
                              value: formData.vehicleStatus,
                              label: statusesData?.results?.find(status => String(status.id) === String(formData.vehicleStatus))?.status_name || formData.vehicleStatus
                            } : null}
                            onChange={(option) => handleInputChange('vehicleStatus', option?.value || "")}
                            options={statusesData?.results?.filter(status => status.status_name.toLowerCase() !== 'deleted').map((status) => ({
                              value: status.id,
                              label: status.status_name
                            })) || []}
                            invalid={!!requiredFieldsTouched.vehicleStatus}
                          />
                        </FormGroup>

                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Color & Mileage Information Card */}
            <Col md={6} >
              <Card>
                <CardHeader className="font-weight-bold text-uppercase">
                  COLOR & MILEAGE
                </CardHeader>
                <CardBody>
                  <Form className={formFieldsClassName}>
                    <Row>
                      <Col md={6}>
                        {/* Exterior Color */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.exteriorColor')}
                          value={formData.exteriorColor}
                          style={inputStyle}
                          onChange={val => handleInputChange('exteriorColor', val)}
                          options={exteriorColors}
                          isCustom={isCustomExteriorColor}
                          setIsCustom={setIsCustomExteriorColor}
                          prevValue={prevValues.exteriorColor}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, exteriorColor: val }))}
                          fieldKey="exteriorColor"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomExteriorColor')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                      <Col md={6}>
                        {/* Interior Color */}
                        <CustomInputOrSelect
                          label={t('addInventoryInformation.interiorColor')}
                          value={formData.interiorColor}
                          style={inputStyle}
                          onChange={val => handleInputChange('interiorColor', val)}
                          options={interiorColors}
                          isCustom={isCustomInteriorColor}
                          setIsCustom={setIsCustomInteriorColor}
                          prevValue={prevValues.interiorColor}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, interiorColor: val }))}
                          fieldKey="interiorColor"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomInteriorColor')}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.acquiredMileage')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input
                            style={inputStyle}
                            type='text'
                            value={formData.acquiredMileage}
                            onChange={(e) => handleInputChange('acquiredMileage', e.target.value)}
                            invalid={!!requiredFieldsTouched.acquiredMileage}
                          />
                          {requiredFieldsTouched.acquiredMileage && (
                            <div style={{ color: 'red', fontSize: 12 }}>{t('addInventoryInformation.required')}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <CustomInputOrSelect
                          label={`${t('addInventoryInformation.mileageStatus')} <span>*</span>`}
                          value={formData.mileageStatus}
                          onChange={val => handleInputChange('mileageStatus', val)}
                          options={mileageStatuses}
                          isCustom={isCustomMileageStatus}
                          setIsCustom={setIsCustomMileageStatus}
                          prevValue={prevValues.mileageStatus}
                          setPrevValue={val => setPrevValues(prev => ({ ...prev, mileageStatus: val }))}
                          fieldKey="mileageStatus"
                          setFormData={setFormData}
                          customPlaceholder={t('addInventoryInformation.enterCustomMileageStatus')}
                          invalid={!!requiredFieldsTouched.mileageStatus}
                          customSelectStyles={resolvedSelectStyles}
                          textInputStyle={inputStyle}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12} className="mt-3 mb-2">
                        <h5 className="font-weight-bold mb-1">Common Colors</h5>
                        <hr className="mt-0" />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.exteriorPrimaryColor')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectPrimaryColor')}
                            name="exteriorPrimaryColor"
                            isSearchable={true}
                            closeMenuOnSelect={true}
                            value={formData.exteriorPrimaryColor ? {
                              value: formData.exteriorPrimaryColor,
                              label: exteriorColors.find(color => color.value === formData.exteriorPrimaryColor)?.label || formData.exteriorPrimaryColor
                            } : null}
                            onChange={(option) => handleInputChange('exteriorPrimaryColor', option?.value || '')}
                            options={exteriorColors}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.exteriorSecondaryColor')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={resolvedSelectStyles}
                            className="react-select"
                            placeholder={t('addInventoryInformation.selectSecondaryColor')}
                            name="exteriorSecondaryColor"
                            isSearchable={true}
                            closeMenuOnSelect={true}
                            value={formData.exteriorSecondaryColor ? {
                              value: formData.exteriorSecondaryColor,
                              label: exteriorColors.find(color => color.value === formData.exteriorSecondaryColor)?.label || formData.exteriorSecondaryColor
                            } : null}
                            onChange={(option) => handleInputChange('exteriorSecondaryColor', option?.value || '')}
                            options={exteriorColors}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>

            </Col>

          </Row>



          {/* VIN Information Card */}
          <Row>
            <Col md={12} >
              <Card>
                <CardHeader className="font-weight-bold text-uppercase">
                  {t('addInventoryInformation.vinInformation')}
                </CardHeader>
                <CardBody>
                  <Form className={formFieldsClassName}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{t('addInventoryInformation.vin')}</Label>
                          <Input
                            style={inputStyle}
                            type="text"
                            value={formData.vin}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <Button
                className="mr-2 d-inline-flex align-items-center justify-content-center"
                style={{ ...activeColorStyle, color: 'white' }}
                onClick={handleSave}
                disabled={createInventoryMutation.isPending}
              >
                {createInventoryMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                    {t('addInventoryInformation.creating')}
                  </>
                ) : (
                  t('addInventoryInformation.createInventory')
                )}
              </Button>
              <Button
                color="secondary"
                outline
                disabled={createInventoryMutation.isPending}
                onClick={() => navigate('/add-inventory')}
              >
                {t('addInventoryInformation.cancel')}
              </Button>
            </Col>
          </Row>
        </>

      }
      <InventoryRightDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        type={formData.acquiredFrom === 'dealer' ? 'dealer_wholesaler_auction' : formData.acquiredFrom}
        initialData={formData.acquired_from_name_customer}
        onSelect={(data) => {
          if (formData.acquiredFrom === 'individual') {
            handleInputChange('acquiredFromName', data.full_name);
            handleInputChange('acquired_from_name_customer', data.customer_id || data.id);
          } else {
            handleInputChange('acquiredFromName', data.name);
            handleInputChange('acquired_from_name_channel', data.id);
          }
        }}
        activeColorStyle={activeColorStyle}
        isLightTheme={isLightTheme}
      />
    </>
  );
};

export default AddInventoryInformation; 